/**
 * COME SI DISPONGONO I CAMPI DI UN TIPO DI ASSENZA — schede e sezioni.
 *
 * Fino al 07-10-2026 i trentaquattro campi stavano in una colonna sola, uno sotto l'altro: la
 * scheda di Lares (`/leave-type`) li divide in tre schede e dieci sezioni con un titolo, ed è il
 * motivo per cui là si legge e qui no. Questo file porta quella divisione, adattata a una cosa
 * nostra: le motivazioni stanno nella loro scheda, perché appartengono al tipo.
 *
 * ⚠️ **È una partizione, e deve restarlo.** Ogni campo dello schema sta in **una** sezione sola:
 * un campo in nessuna sparisce dalla scheda senza errore — si salva col valore di prima e
 * nessuno lo vede più — e uno in due si modifica da due posti che si contraddicono.
 * `tests/unit/leaveTypeSections.test.ts` lo controlla contro `LEAVE_TYPE_SCHEMA`: un campo
 * aggiunto allo schema e dimenticato qui fa rosso.
 *
 * Le definizioni dei campi — etichette, spiegazioni, regole — **restano nello schema**. Qui ci
 * sono solo i nomi e l'ordine.
 */

import { APPROVAL_WORKFLOW_LABEL } from './approvalWorkflow';
import { whereSummary } from '@/features/organization/scope/WhereItApplies.utils';
import {
  ACCRUAL_LABEL, AUTO_APPROVE_LABEL, CARRY_OVER_LABEL, DOC_SUBMISSION_LABEL, DOCUMENT_TYPE_LABEL,
  LEAVE_CATEGORY_LABEL, LEAVE_ELIGIBILITY_LABEL, LEAVE_ELIGIBILITY_MODE_LABEL, UOM_LABEL,
} from './leaveApi';

export type LeaveTypeTab = 'data' | 'eligibility' | 'reasons';

type Values = Record<string, unknown>;

export interface LeaveTypeSection {
  /**
   * Un nome stabile, da mettere nell'indirizzo (`?section=approval`): il titolo è una scritta, e
   * una scritta che cambia romperebbe i link già mandati.
   */
  key: string;
  title: string;
  /** Una riga sotto il titolo: a cosa serve la sezione, non cosa c'è dentro. */
  sub?: string;
  fields: readonly string[];
  /**
   * La sezione chiusa, in una riga: i valori che contano, scritti come si direbbero.
   *
   * ⚠️ **È quel che rende l'accordion un riassunto e non un nascondiglio** (07-10-2026). Senza,
   * una sezione chiusa dice solo il suo titolo, e per sapere se il riporto scade bisogna aprirla.
   * Con, la scheda chiusa si legge come la riga dell'elenco, e si apre solo quel che si cambia.
   */
  summary: (v: Values) => string;
}

/* ──────────────── i pezzi delle frasi ──────────────── */

const num = (x: unknown): number | null => {
  const n = Number(x);
  return x !== '' && x != null && Number.isFinite(n) && n > 0 ? n : null;
};
const label = (map: Record<string, string>, x: unknown) => (x ? map[String(x)] ?? String(x) : null);
const join = (parts: (string | number | null | false | undefined)[]) => parts.filter(Boolean).join(' · ') || '—';

/** Una coppia «sempre / mai / oltre N giorni», com'è salvata (flag + soglia). */
const threshold = (flag: unknown, after: unknown): string => {
  if (flag) return 'sempre';
  const n = num(after);
  return n ? `da ${n} gg` : 'mai';
};

/** Scheda «Dati del tipo». */
export const LEAVE_TYPE_DATA_SECTIONS: readonly LeaveTypeSection[] = [
  {
    key: 'identity',
    title: 'Identità',
    fields: ['code', 'name', 'description', 'category'],
    summary: (v) => join([v.code ? String(v.code) : null, label(LEAVE_CATEGORY_LABEL, v.category)]),
  },
  {
    key: 'measure',
    title: 'Misura e maturazione',
    sub: 'Come si conta l’assenza e se esiste un monte da consumare.',
    fields: ['unitOfMeasure', 'accrualPolicy', 'accrualRatePerMonth', 'entitledDaysPerYear', 'entitledDaysPerEvent', 'maxEventsPerYear'],
    summary: (v) => join([
      label(UOM_LABEL, v.unitOfMeasure),
      v.accrualPolicy ? `maturazione ${(label(ACCRUAL_LABEL, v.accrualPolicy) ?? '').toLowerCase()}` : null,
      num(v.accrualRatePerMonth) && `${num(v.accrualRatePerMonth)} gg al mese`,
      num(v.entitledDaysPerYear) && `${num(v.entitledDaysPerYear)} gg/anno`,
      num(v.entitledDaysPerEvent) && `${num(v.entitledDaysPerEvent)} gg a evento`,
      num(v.maxEventsPerYear) && `max ${num(v.maxEventsPerYear)} volte l’anno`,
    ]),
  },
  {
    key: 'carryOver',
    title: 'Riporto all’anno successivo',
    fields: ['carryOverPolicy', 'carryOverExpirationMonths'],
    summary: (v) => (v.carryOverPolicy === 'expiring'
      ? `con scadenza${num(v.carryOverExpirationMonths) ? ` dopo ${num(v.carryOverExpirationMonths)} mesi` : ''}`
      : v.carryOverPolicy === 'none' ? 'il residuo si perde' : (label(CARRY_OVER_LABEL, v.carryOverPolicy) ?? '—').toLowerCase()),
  },
  {
    key: 'limits',
    title: 'Preavviso e limiti',
    fields: ['minAdvanceDays', 'maxConsecutiveDays', 'allowsPartialCancellation'],
    summary: (v) => join([
      num(v.minAdvanceDays) ? `preavviso ${num(v.minAdvanceDays)} gg` : 'nessun preavviso',
      num(v.maxConsecutiveDays) && `max ${num(v.maxConsecutiveDays)} gg di fila`,
      Boolean(v.allowsPartialCancellation) && 'si cancella anche per giorni',
    ]),
  },
  {
    key: 'documents',
    title: 'Documentazione richiesta',
    fields: ['requiresDocument', 'documentRequiredAfterDays', 'documentSubmissionMode'],
    summary: (v) => (!v.requiresDocument || v.requiresDocument === 'none'
      ? 'nessun documento'
      : join([
        label(DOCUMENT_TYPE_LABEL, v.requiresDocument),
        num(v.documentRequiredAfterDays) ? `dopo ${num(v.documentRequiredAfterDays)} gg` : 'subito',
        label(DOC_SUBMISSION_LABEL, v.documentSubmissionMode)?.toLowerCase(),
      ])),
  },
  {
    key: 'substitute',
    title: 'Sostituto e passaggio di consegne',
    fields: ['requiresSubstitute', 'requiresHandover'],
    summary: (v) => `sostituto ${threshold(v.requiresSubstitute, v.substituteRequiredAfterDays)} · consegne ${threshold(v.requiresHandover, v.handoverRequiredAfterDays)}`,
  },
  {
    key: 'approval',
    title: 'Approvazione',
    sub: 'Chi deve dire di sì, e se si può dire di no.',
    fields: ['autoApprovePolicy', 'approvalWorkflow', 'isRejectable', 'requiresReason'],
    summary: (v) => join([
      v.autoApprovePolicy && v.autoApprovePolicy !== 'never'
        ? `si approva da sola: ${(label(AUTO_APPROVE_LABEL, v.autoApprovePolicy) ?? '').toLowerCase()}`
        : null,
      v.approvalWorkflow ? label(APPROVAL_WORKFLOW_LABEL, v.approvalWorkflow)?.toLowerCase() : 'come l’azienda',
      v.isRejectable === false ? 'non rifiutabile' : 'rifiutabile',
      Boolean(v.requiresReason) && 'chiede la motivazione',
    ]),
  },
  {
    key: 'payroll',
    title: 'Retribuzione e paghe',
    fields: ['paidLeave', 'impactsPayroll', 'suspendsObligations'],
    summary: (v) => join([v.paidLeave ? 'retribuita' : 'non retribuita', Boolean(v.impactsPayroll) && 'va in busta paga']),
  },
  {
    key: 'appearance',
    title: 'Aspetto e stato',
    fields: ['icon', 'color', 'sortOrder', 'isActive'],
    summary: (v) => join([v.isActive === false ? 'spento' : 'attivo', num(v.sortOrder) && `posizione ${num(v.sortOrder)}`]),
  },
];

/** Scheda «Chi può richiederlo». */
export const LEAVE_TYPE_ELIGIBILITY_SECTIONS: readonly LeaveTypeSection[] = [
  {
    key: 'eligibility',
    title: 'Chi può richiederlo',
    sub: 'A chi compare il tipo nella tendina, e chi ha un saldo da consumare.',
    fields: ['eligibilityMode', 'requiredProtectedStatusTypes', 'requiredProtectedStatusSubTypes', 'eligibility'],
    summary: (v) => join([label(LEAVE_ELIGIBILITY_MODE_LABEL, v.eligibilityMode), label(LEAVE_ELIGIBILITY_LABEL, v.eligibility)]),
  },
  {
    key: 'where',
    title: 'Dove vale',
    sub: 'Solo in questa azienda, in tutto il gruppo o in alcune aziende del gruppo.',
    fields: ['scope'],
    summary: (v) => whereSummary(v.scope, v.scopeTenantIds, () => null),
  },
];

/** Le chiavi che un indirizzo può chiedere di aprire. */
export const LEAVE_TYPE_SECTION_KEYS: readonly string[] = LEAVE_TYPE_DATA_SECTIONS.map((sec) => sec.key);
